<template>
  <v-chart ref="chart" autoresize :option="chartOptions"> </v-chart>
</template>
<script>
import { use } from 'echarts/core'
import { CanvasRenderer } from 'echarts/renderers'
import { ScatterChart } from 'echarts/charts'
import {
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  GridComponent,
  SingleAxisComponent,
  MarkLineComponent
} from 'echarts/components'
import VChart from 'vue-echarts'

use([
  CanvasRenderer,
  ScatterChart,
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  GridComponent,
  SingleAxisComponent,
  MarkLineComponent
])

export default {
  name: 'RiskAnalysisChart',
  components: {
    VChart
  },
  props: {
    value: {
      type: Array,
      description: 'Array of assets'
    },
    probabilityThreshold: {
      type: Number,
      description: 'Probability of failure value for chart',
      default: 50
    }
  },
  computed: {
    seriesData: function () {
      if (!this.value) return []

      let computableAssets = this.value.filter(
        v =>
          v.consequenceValue !== null &&
          v.consequenceValue !== undefined &&
          !isNaN(v.consequenceValue) &&
          v.probabilityValue !== null &&
          v.probabilityValue !== undefined &&
          !isNaN(v.probabilityValue)
      )

      let maxCost = Math.max.apply(
        Math,
        computableAssets.map(x => x.consequenceValue)
      )
      return computableAssets.map(asset => {
        let currentPof = parseInt(asset.probabilityValue)

        return {
          value: [currentPof, Math.max(parseInt(((asset.consequenceValue / maxCost) * 100) / 2), 5)],
          assetName: asset.assetName,
          consequenceValue: asset.consequenceValue,
          itemStyle: {
            color: currentPof >= this.probabilityThreshold ? this.abovePofColor : this.belowPofColor,
            border: '2px',
            borderColor: currentPof >= this.probabilityThreshold ? this.abovePofBorderColor : this.belowPofBorderColor
          }
        }
      })
    },
    chartOptions: function () {
      let singleAxis = [
        {
          height: '60px',
          left: '20px',
          right: '20px',
          top: '0px',
          bottom: '0px',
          type: 'category',
          boundaryGap: false,
          data: [...Array(101).keys()].map(x => String(x) + '%'),
          axisLabel: {
            interval: 9
          }
        }
      ]

      return {
        tooltip: {
          trigger: 'axis',
          formatter: args => {
            return this.formatTooltip(args)
          },
          extraCssText: 'overflow-y:auto;max-height:175px;',
          confine: true,
          enterable: true
        },
        singleAxis: singleAxis,
        series: [
          {
            singleAxisIndex: 0,
            coordinateSystem: 'singleAxis',
            type: 'scatter',
            data: this.seriesData,
            symbolSize: function (dataItem) {
              return dataItem[1]
            }
          }
        ]
      }
    }
  },
  data() {
    return {
      abovePofBorderColor: 'rgb(46, 125, 50)',
      abovePofColor: 'rgba(46, 125, 50, 0.4)',
      belowPofBorderColor: 'rgb(15, 63, 114)',
      belowPofColor: 'rgba(15, 63, 114, 0.4)'
    }
  },
  methods: {
    groupBy(items, key) {
      return items.reduce((itemsByKeyValue, item) => {
        const value = item[key]
        itemsByKeyValue[value] = (itemsByKeyValue[value] || []).concat(item)
        return itemsByKeyValue
      }, {})
    },
    formatTooltip(args) {
      if (!args || args.length < 1) return ''

      let markerItems = args
        .map(arg => {
          return {
            marker: arg.marker,
            assetName: arg.data.assetName,
            consequenceValue: arg.data.consequenceValue,
            probabilityValue: arg.data.value[0]
          }
        })
        .sort((a, b) => (a.consequenceValue > b.consequenceValue ? -1 : 1))

      let tooltip = ''
      markerItems.forEach(item => {
        tooltip += `<div>${item.marker} ${item.assetName} - $${item.consequenceValue.toLocaleString()}</div>`
      })
      return `<div style="text-align:left"><strong>${markerItems[0].probabilityValue}% probability</strong></div>${tooltip}`
    }
  }
}
</script>
<style lang="css" scoped>
.full-header {
  width: 100%;
}
.echarts {
  height: 80px;
}
</style>
